import React from "react";
import Button, { ButtonDesign } from "../../../../components/Button/Button";
import styles from "./WarehouseStats.module.css";

interface WarehouseStatsData {
  productsTotal: number;
  boxesTotal: number;
  productsInBoxes: number;
  productsWithoutBox: number;
  expiringSoon: number;
  expired: number;
}

interface WarehouseAdminSummaryData {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  deletedProducts: number;
  deletedBoxes: number;
}

export type WarehouseStatsCardKey =
  | "productsTotal"
  | "boxesTotal"
  | "productsInBoxes"
  | "productsWithoutBox"
  | "expiringSoon"
  | "expired"
  | "users"
  | "deleted";

interface WarehouseStatsProps {
  stats: WarehouseStatsData;
  isAdmin?: boolean;
  adminSummary?: WarehouseAdminSummaryData | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onOpenList: (key: WarehouseStatsCardKey) => void;
}

interface StatsCard {
  key: WarehouseStatsCardKey;
  label: string;
  value: number;
  hint?: string;
  attention?: boolean;
}

export const WarehouseStats: React.FC<WarehouseStatsProps> = ({
  stats,
  isAdmin = false,
  adminSummary,
  isLoading,
  error,
  onRefresh,
  onOpenList,
}) => {
  const cards: StatsCard[] = [
    {
      key: "productsTotal" as const,
      label: "продуктов",
      value: stats.productsTotal,
    },
    { key: "boxesTotal" as const, label: "коробок", value: stats.boxesTotal },
    {
      key: "productsInBoxes" as const,
      label: "в коробках",
      value: stats.productsInBoxes,
    },
    {
      key: "productsWithoutBox" as const,
      label: "без коробки",
      value: stats.productsWithoutBox,
    },
    {
      key: "expiringSoon" as const,
      label: "истекают <=7д",
      value: stats.expiringSoon,
    },
    { key: "expired" as const, label: "просрочены", value: stats.expired },
  ];

  if (isAdmin) {
    cards.push({
      key: "users" as const,
      label: "пользователи",
      value: adminSummary?.totalUsers ?? 0,
      hint:
        (adminSummary?.pendingUsers ?? 0) > 0
          ? `активные: ${adminSummary?.activeUsers ?? 0} · новые: ${
              adminSummary?.pendingUsers ?? 0
            }`
          : `активные: ${adminSummary?.activeUsers ?? 0}`,
      attention: (adminSummary?.pendingUsers ?? 0) > 0,
    });
    cards.push({
      key: "deleted" as const,
      label: "удаленные",
      value:
        (adminSummary?.deletedProducts ?? 0) +
        (adminSummary?.deletedBoxes ?? 0),
      hint: `товары: ${adminSummary?.deletedProducts ?? 0} · коробки: ${
        adminSummary?.deletedBoxes ?? 0
      }`,
      attention: false,
    });
  }

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <strong className={styles.title}>Статистика склада</strong>
        <Button
          design={ButtonDesign.ghost}
          className={styles.refresh}
          onClick={onRefresh}
          disabled={isLoading}
        >
          обновить
        </Button>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {isLoading && <p className={styles.loading}>Загружаем статистику...</p>}

      {!isLoading && (
        <div className={styles.grid}>
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              className={styles.card}
              onClick={() => onOpenList(card.key)}
              aria-label={`Открыть список: ${card.label}`}
            >
              <p className={styles.value}>{card.value}</p>
              <div className={styles.labelRow}>
                <p className={styles.label}>{card.label}</p>
                {card.attention ? (
                  <span
                    className={styles.attentionMark}
                    aria-label="Есть новые заявки"
                  >
                    !
                  </span>
                ) : null}
              </div>
              {card.hint ? <p className={styles.hint}>{card.hint}</p> : null}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};
